import { jsPDF } from "jspdf";
import type { Event, EventPackingItem } from "@satyrsmc/shared/client";
import { formatDateLong } from "@/lib/date-utils";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** Export the current load out packing list as a simple text PDF, grouped by
 * category, with each item printed next to a checkbox so it can be ticked
 * off by hand while loading. */
export function downloadPackingPdf(event: Event): void {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = 612;
  const pageHeight = 792;
  const margin = 54;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 14;
  const checkboxSize = 9;

  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(event.name, margin, y);
  y += lineHeight + 4;

  if (event.start_date) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(formatDateLong(event.start_date), margin, y);
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 8;
  } else {
    y += 8;
  }

  const categories = [...(event.packingCategories ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );
  const items = event.packingItems ?? [];

  for (const category of categories) {
    const categoryItems = items
      .filter((i) => i.category_id === category.id)
      .sort(
        (a: EventPackingItem, b: EventPackingItem) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
      );
    if (categoryItems.length === 0) continue;

    ensureSpace(lineHeight * 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(category.name, margin, y);
    y += lineHeight + 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    for (const item of categoryItems) {
      const label =
        item.quantity != null && item.quantity > 0 ? `${item.name} ×${item.quantity}` : item.name;
      const wrapped = doc.splitTextToSize(label, contentWidth - 14 - checkboxSize);
      ensureSpace(lineHeight * wrapped.length);
      doc.rect(margin, y - checkboxSize + 2, checkboxSize, checkboxSize);
      doc.text(wrapped, margin + checkboxSize + 6, y);
      y += lineHeight * wrapped.length;

      if (item.note) {
        doc.setTextColor(100, 100, 100);
        const wrappedNote = doc.splitTextToSize(item.note, contentWidth - 14 - checkboxSize);
        ensureSpace(lineHeight * wrappedNote.length);
        doc.text(wrappedNote, margin + checkboxSize + 6, y);
        doc.setTextColor(0, 0, 0);
        y += lineHeight * wrappedNote.length;
      }
    }
    y += 8;
  }

  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const slug = slugify(event.name);
  a.download = slug ? `packing-list-${slug}.pdf` : "packing-list.pdf";
  a.click();
  URL.revokeObjectURL(url);
}
